/**
 * Filtered transfer service
 */
import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  IEntityConfig,
  ENTITY_TYPES,
  IXVIListColumn,
  CELL_FILTER,
  CELL_RENDERER,
  COLUMN_SELECTOR_ID,
  FilterOptionType,
  FilterItemsSourceEnum,
  TRANSFER_FILTER_STATUS_OPTIONS,
  TRANSFER_STATUS_ENUM_OPTIONS,
} from '@shared/types';

import { getSingularEntityName } from '@app/shared/configs/entity-name-config';
import { filter, includes, merge } from 'lodash';
import { detectMobile } from '@geniuspos/ui-shared/modules/xdm-shared-components/components/utils';
import { TransferService } from '../transfer/transfer.service';
import {
  filterTransferParamsParser,
  generalTypeParser,
} from '../filtered-order/filtered.util';
import { transferServiceConfig } from '../transfer/transfer.service.config';
import { OPEN_AND_PENDING_STATUS } from '../transfer/transfer-constants';
import { transferTypeFormatter } from '../transfer/transfer.util';
import { TRANSFER_STATUS_OPTIONS } from '@app/shared/types/enums/transfer-status.enum';
import { TRANSFER_UNSAVED_STATUS } from '../transfer/transfer.definition';
import { I18n } from '@app/i18n/strings-keys';

const entityType: ENTITY_TYPES = ENTITY_TYPES.FILTERED_TRANSFER;
const filteredTransferServiceConfig: IEntityConfig = merge(
  {},
  transferServiceConfig,
  <IEntityConfig>{
    ENTITY_TYPE: entityType,
    entityNameToCheckPermissions: ENTITY_TYPES.TRANSFER,
    uiRoute: entityType,
    isMasterListAvailable: true,
    buttons: {},
    editor: {
      enableEntryDuplicate: false,
      entityTitle: getSingularEntityName(entityType),
    },
    apiEndpoint: ENTITY_TYPES.TRANSFER,
    isFormServiceSupported: false,
    list: {
      gridOptions: {
        valueField: '_id',
        multiselect: true,
      },
      isShowModifiedDateByDefault: true,
      isHideModifiedDateFilter: true,
      defaultViewFieldsSetting: {
        requiredSetting: {
          fields: ['uid', 'type', 'date_from', 'date_to'],
        },
        filterModelSetting: {
          status: {
            dependencyField: ['type'],
            fieldsParser: generalTypeParser,
          },
        },
        filterQuerySetting: {
          dependencyField: ['type', 'date_from', 'date_to', 'uid'],
          fieldsParser: filterTransferParamsParser,
        },
        validateMessage: I18n.ErrorMessages.IncorrectUrlMessage,
      },
      documentQuery: {
        orderby: 'updated_at desc',
        notNeedActive: true,
        select: ['site_id', 'updated_items', 'unavailable_items'],
      },
      columns: [],
      titleTooltipIconConfig$: new BehaviorSubject({
        tooltipConfig: {
          tooltip: I18n.InfoMessages.TransferFilterListTooltip,
        },
      }),
    },
  },
);

/**
 * Filtered Transfer service that extends TransferService to see
 * filtered transfers from emails
 */
@Injectable()
export class FilteredTransferService extends TransferService {
  public static config: IEntityConfig = filteredTransferServiceConfig;
  public static ENTITY_TYPE: string = filteredTransferServiceConfig.ENTITY_TYPE;

  public constructor(public injector: Injector) {
    super(injector);

    this.config.buttons.create = {
      hidden: true,
    };
    this.config.buttons.auto_restock = {
      hidden: true,
    };
    this.config.buttons.print_sheets = {
      hidden: true,
    };

    const listColumns: IXVIListColumn[] = [
      {
        path: 'transaction_date',
        title: I18n.Common.Date,
        filterType: CELL_FILTER.dateLocale,
        sortable: true,
        defaultView: true,
      },
      {
        path: 'internal_number',
        title: I18n.Forms.GridForm.ID,
        filterType: 'stringFilter',
        filterParams: {
          quickFilter: true,
        },
        sortable: true,
        defaultView: true,
      },
      {
        path: 'site_name',
        title: I18n.Forms.TransferForm.FromName,
        filterType: 'stringFilter',
        filterParams: {
          quickFilter: true,
        },
        cellRenderer: CELL_RENDERER.link_renderer,
        cellRendererParams: {
          entityType,
          needGetUpdateItemsStatus: OPEN_AND_PENDING_STATUS,
        },
        sortable: true,
        defaultView: true,
      },
      {
        path: 'store_number',
        title: I18n.Forms.TransferForm.FromID,
        filterType: 'stringFilter',
        filterParams: {
          quickFilter: true,
        },
        cellRenderer: CELL_RENDERER.link_renderer,
        cellRendererParams: {
          entityType,
          needGetUpdateItemsStatus: OPEN_AND_PENDING_STATUS,
        },
        sortable: true,
        defaultView: true,
      },
      {
        path: 'site_name_to',
        title: I18n.Forms.TransferForm.ToName,
        filterType: 'stringFilter',
        filterParams: {
          quickFilter: true,
        },
        cellRenderer: CELL_RENDERER.link_renderer,
        cellRendererParams: {
          entityType,
          needGetUpdateItemsStatus: OPEN_AND_PENDING_STATUS,
        },
        sortable: true,
        defaultView: true,
      },
      {
        path: 'store_number_to',
        title: I18n.Forms.TransferForm.ToID,
        filterType: 'stringFilter',
        filterParams: {
          quickFilter: true,
        },
        cellRenderer: CELL_RENDERER.link_renderer,
        cellRendererParams: {
          entityType,
          needGetUpdateItemsStatus: OPEN_AND_PENDING_STATUS,
        },
        sortable: true,
        defaultView: true,
      },
      {
        path: 'transfer_type',
        title: I18n.Common.Type,
        filterType: CELL_FILTER.enum,
        filterParams: {
          values: TRANSFER_FILTER_STATUS_OPTIONS,
        },
        sortable: true,
        valueFormatter: (params) =>
          transferTypeFormatter(params, { i18nService: this.i18nService }),
        defaultView: true,
      },
      {
        path: 'event_id',
        title: I18n.EntityNames.Event,
        defaultView: true,
        filterType: CELL_FILTER.entity,
        filterParams: {
          default: {
            type: 'containsAny' as FilterOptionType,
          },
          filterItemsSource: FilterItemsSourceEnum.XPRT_EVENTS,
          filterOptions: ['containsAny'] as FilterOptionType[],
          filterItemSourceQuery: {
            select: ['name', 'id'],
          },
          suppressUIFiltering: true,
        },
      },
      {
        path: 'status',
        title: I18n.Common.Status,
        sortable: true,
        defaultView: true,
        filterType: CELL_FILTER.enum,
        filterParams: {
          values: filter(
            TRANSFER_STATUS_OPTIONS,
            (item) => !includes(TRANSFER_UNSAVED_STATUS, item.value),
          ),
        },
        headerComponentParams: {
          enableMenu: false,
        },
        cellRenderer: CELL_RENDERER.prepared_string_renderer,
        cellRendererParams: {
          valueToEnumMap: TRANSFER_STATUS_ENUM_OPTIONS,
        },
      },
      {
        path: COLUMN_SELECTOR_ID,
        width: 50,
        title: '',
        defaultView: true,
        cellRenderer: CELL_RENDERER.filtered_transfer_actions_render,
        pinned: detectMobile() ? false : 'right',
        lockPinned: true,
      },
    ];

    this.config.list.columns = [...listColumns];
  }
}
